import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const CONTRATO_MUNICIPIOS_KEY = ['emp-contrato-municipios'];
const EMPTY_CODES: string[] = [];

export function useContratoMunicipios(contratoId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: [...CONTRATO_MUNICIPIOS_KEY, contratoId],
    queryFn: async () => {
      if (!contratoId) return EMPTY_CODES;
      const { data, error } = await supabase
        .from('emp_contrato_municipios')
        .select('municipio_ibge')
        .eq('contrato_id', contratoId);
      if (error) throw error;
      return (data || []).map(r => r.municipio_ibge);
    },
    enabled: !!contratoId,
  });

  const municipioCodes = useMemo(() => data ?? EMPTY_CODES, [data]);

  const syncMunicipios = useMutation({
    mutationFn: async ({ contratoId, codes }: { contratoId: string; codes: string[] }) => {
      const { error: delErr } = await supabase
        .from('emp_contrato_municipios')
        .delete()
        .eq('contrato_id', contratoId);
      if (delErr) throw delErr;

      if (codes.length > 0) {
        const rows = codes.map(c => ({ contrato_id: contratoId, municipio_ibge: c }));
        const { error: insErr } = await supabase
          .from('emp_contrato_municipios')
          .insert(rows);
        if (insErr) throw insErr;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONTRATO_MUNICIPIOS_KEY });
      toast({ title: 'Cidades de execução atualizadas' });
    },
    onError: (err: Error) => {
      toast({ title: 'Erro ao atualizar cidades', description: err.message, variant: 'destructive' });
    },
  });

  return { municipioCodes, isLoading, syncMunicipios };
}

